import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  Megaphone, Zap, Clock, Star, Bike, Car, Truck, Check, Loader2, MapPin,
} from 'lucide-react'
import { offersAPI, type OfferView } from '../../api/offers'
import { getSocket } from '../../socket/socketClient'

interface OffersPanelProps {
  deliveryId: string
  status: string
  region?: string
  onChanged: () => void
}

const fmtPickup = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })

function VehicleIcon({ vehicle }: { vehicle?: string }) {
  const size = 14
  if (vehicle === 'moto') return <Bike size={size} />
  if (vehicle === 'voiture') return <Car size={size} />
  if (vehicle === 'camionnette' || vehicle === 'camion') return <Truck size={size} />
  return <Bike size={size} />
}

export function OffersPanel({ deliveryId, status, region, onChanged }: OffersPanelProps) {
  const qc = useQueryClient()
  const isMatching = status === 'matching'
  const isValidated = status === 'validated'

  const { data: offers, isLoading } = useQuery({
    queryKey: ['offers', deliveryId],
    queryFn: () => offersAPI.getOffers(deliveryId),
    enabled: isMatching,
    refetchInterval: isMatching ? 15_000 : false,
  })

  // Temps réel : nouvelles offres / retrait / assignation
  useEffect(() => {
    if (!isMatching) return
    const socket = getSocket()
    if (!socket) return

    const refreshOffers = (p: { deliveryId?: string } = {}) => {
      if (p.deliveryId && p.deliveryId !== deliveryId) return
      qc.invalidateQueries({ queryKey: ['offers', deliveryId] })
    }
    const onNew = (p: { deliveryId?: string } = {}) => {
      refreshOffers(p)
      if (!p.deliveryId || p.deliveryId === deliveryId) toast('Nouvelle offre reçue', { icon: '📨' })
    }
    const onAssigned = (p: { deliveryId?: string } = {}) => {
      if (p.deliveryId && p.deliveryId !== deliveryId) return
      onChanged()
    }

    const onExpired = (p: { deliveryId?: string } = {}) => {
      if (p.deliveryId && p.deliveryId !== deliveryId) return
      toast('Diffusion expirée — aucune offre reçue', { icon: '⏱️' })
      onChanged()
    }

    socket.on('offer:new', onNew)
    socket.on('offer:withdrawn', refreshOffers)
    socket.on('delivery:assigned', onAssigned)
    socket.on('broadcast:expired', onExpired)
    return () => {
      socket.off('offer:new', onNew)
      socket.off('offer:withdrawn', refreshOffers)
      socket.off('delivery:assigned', onAssigned)
      socket.off('broadcast:expired', onExpired)
    }
  }, [deliveryId, isMatching, qc, onChanged])

  const broadcast = useMutation({
    mutationFn: () => offersAPI.broadcast(deliveryId),
    onSuccess: ({ region: r, notified }) => {
      toast.success(`Diffusé à ${notified} livreur(s) — ${r}`)
      onChanged()
    },
  })

  const selectOne = useMutation({
    mutationFn: (offerId: string) => offersAPI.select(deliveryId, offerId),
    onSuccess: () => {
      toast.success('Livreur sélectionné')
      qc.invalidateQueries({ queryKey: ['offers', deliveryId] })
      onChanged()
    },
  })

  const auto = useMutation({
    mutationFn: () => offersAPI.autoSelect(deliveryId),
    onSuccess: () => {
      toast.success('Livreur le plus rapide sélectionné')
      qc.invalidateQueries({ queryKey: ['offers', deliveryId] })
      onChanged()
    },
  })

  if (!isMatching && !isValidated) return null

  // ── Phase 1 : colis validé, pas encore diffusé ──────────────────────────────
  if (isValidated) {
    return (
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={iconBubble}><Megaphone size={18} color="#2563EB" /></div>
          <div style={{ flex: 1 }}>
            <div style={titleStyle}>Trouver un livreur</div>
            <div style={subStyle}>Diffusez ce colis aux livreurs de sa région pour recevoir des offres.</div>
          </div>
        </div>
        <button
          className="btn"
          style={broadcastBtn}
          disabled={broadcast.isPending}
          onClick={() => broadcast.mutate()}
        >
          {broadcast.isPending ? <Loader2 size={15} className="spin" /> : <Megaphone size={15} />}
          Diffuser aux livreurs
        </button>
      </div>
    )
  }

  // ── Phase 2 : en diffusion, offres en cours ─────────────────────────────────
  const list = offers ?? []
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={titleStyle}>Offres reçues</span>
          <span style={countPill}>{list.length}</span>
        </div>
        <button
          className="btn"
          style={{ ...autoBtn, opacity: list.length === 0 || auto.isPending ? 0.5 : 1 }}
          disabled={list.length === 0 || auto.isPending}
          onClick={() => auto.mutate()}
        >
          {auto.isPending ? <Loader2 size={14} className="spin" /> : <Zap size={14} />}
          Choisir le plus rapide
        </button>
      </div>

      {isLoading ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Chargement…</div>
      ) : list.length === 0 ? (
        <div style={emptyBox}>
          <Clock size={22} color="#9CA3AF" />
          <div style={{ fontSize: 13, color: '#6B7280' }}>
            En attente d'offres des livreurs{region ? ` · zone ${region}` : ''}…
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {list.map((o: OfferView) => (
            <div key={o._id} style={{ ...offerCard, ...(o.isFastest ? fastestRing : {}) }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#0D1117' }}>{o.livreur.name}</span>
                  <span style={ratingChip}><Star size={11} fill="#F3A845" color="#F3A845" /> {o.livreur.rating.toFixed(1)}</span>
                  <span style={vehicleChip}><VehicleIcon vehicle={o.livreur.vehicle} /></span>
                </div>
                {o.isFastest && (
                  <span style={fastestBadge}><Zap size={11} /> Le plus rapide</span>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, color: '#374151', fontSize: 13 }}>
                <Clock size={14} color="#6B7280" />
                <span style={{ fontWeight: 600 }}>Récupération : {fmtPickup(o.proposedPickupAt)}</span>
              </div>

              {o.livreur.distanceKm != null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4, color: '#9CA3AF', fontSize: 12 }}>
                  <MapPin size={12} /> à ~{o.livreur.distanceKm} km du ramassage
                </div>
              )}

              {o.note && <div style={noteStyle}>“{o.note}”</div>}

              <button
                className="btn"
                style={selectBtn}
                disabled={selectOne.isPending}
                onClick={() => selectOne.mutate(o._id)}
              >
                {selectOne.isPending ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
                Choisir ce livreur
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Styles inline (cohérents avec la page détail) ─────────────────────────────
const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #EEF1F4', borderRadius: 16,
  padding: 18, marginBottom: 16,
}
const iconBubble: React.CSSProperties = {
  width: 38, height: 38, borderRadius: 10, background: '#EFF6FF',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
}
const titleStyle: React.CSSProperties = { fontSize: 15, fontWeight: 700, color: '#0D1117' }
const subStyle: React.CSSProperties = { fontSize: 12.5, color: '#6B7280', marginTop: 2 }
const broadcastBtn: React.CSSProperties = {
  marginTop: 14, width: '100%', justifyContent: 'center', gap: 8,
  background: '#2563EB', color: '#fff', border: 'none', fontWeight: 600,
}
const countPill: React.CSSProperties = {
  background: '#EEF7E2', color: '#5d9933', borderRadius: 99,
  padding: '1px 9px', fontSize: 12, fontWeight: 700,
}
const autoBtn: React.CSSProperties = {
  gap: 6, background: '#7CB342', color: '#fff', border: 'none',
  fontWeight: 600, fontSize: 12.5, padding: '7px 12px',
}
const emptyBox: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
  padding: '26px 0', textAlign: 'center',
}
const offerCard: React.CSSProperties = {
  border: '1px solid #EEF1F4', borderRadius: 12, padding: 14, background: '#FCFDFE',
}
const fastestRing: React.CSSProperties = { borderColor: '#7CB342', boxShadow: '0 0 0 1px #7CB342' }
const ratingChip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, color: '#6B7280', fontWeight: 600,
}
const vehicleChip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', color: '#6B7280',
}
const fastestBadge: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3, background: '#EEF7E2',
  color: '#5d9933', borderRadius: 99, padding: '2px 8px', fontSize: 11, fontWeight: 700,
}
const noteStyle: React.CSSProperties = {
  marginTop: 8, fontSize: 12.5, color: '#6B7280', fontStyle: 'italic',
}
const selectBtn: React.CSSProperties = {
  marginTop: 12, width: '100%', justifyContent: 'center', gap: 6,
  background: '#0A1E3A', color: '#fff', border: 'none', fontWeight: 600, fontSize: 13,
}
